// TODO: align with world-map.ts — RoomData (in src/engine/constants.ts) does
// not yet have native fields for encounterZones, exits, or trainers. This
// module extends RoomData with those map-meta fields so a future map-router
// agent can wire Route 1 into the world. When constants.ts is updated to
// include these fields, switch this file to import the canonical type.

import { TileType, type RoomData } from "@/engine/constants";

// ─── Extended map-meta types ──────────────────────────────────────────────────

export interface EncounterPoolEntry {
  speciesId: string;
  weight: number;
  levelMin: number;
  levelMax: number;
}

export interface EncounterZone {
  id: string;
  bounds: { x: number; y: number; w: number; h: number };
  speciesPool: EncounterPoolEntry[];
}

export interface MapExit {
  x: number;
  y: number;
  targetMapId: string;
  targetX: number;
  targetY: number;
}

export interface TrainerPlacement {
  trainerId: string;
  x: number;
  y: number;
}

export interface RouteMap extends RoomData {
  encounterZones: EncounterZone[];
  exits: MapExit[];
  trainers: TrainerPlacement[];
}

// ─── Layout constants ─────────────────────────────────────────────────────────

const WIDTH = 24;
const HEIGHT = 32;

// Boardwalk is an 8-tile-wide vertical strip down the middle.
const BOARDWALK_X = 8;
const BOARDWALK_W = 8;

// Encounter zones flank the boardwalk (tall grass).
const WEST_GRASS_BOUNDS = { x: 0, y: 2, w: BOARDWALK_X, h: HEIGHT - 4 };
const EAST_GRASS_BOUNDS = {
  x: BOARDWALK_X + BOARDWALK_W,
  y: 2,
  w: WIDTH - (BOARDWALK_X + BOARDWALK_W),
  h: HEIGHT - 4,
};

// Impassable props (palms + tiki bars) placed in the grass strips.
// All coords are tile-space and must lie OUTSIDE the boardwalk strip.
interface PropPlacement {
  x: number;
  y: number;
  tile: TileType;
}

const PROPS: PropPlacement[] = [
  // West-side palms (trunk + leaves above)
  { x: 2, y: 5, tile: TileType.PalmTrunk },
  { x: 2, y: 4, tile: TileType.PalmLeaves },
  { x: 5, y: 14, tile: TileType.PalmTrunk },
  { x: 5, y: 13, tile: TileType.PalmLeaves },
  { x: 1, y: 22, tile: TileType.PalmTrunk },
  { x: 1, y: 21, tile: TileType.PalmLeaves },

  // East-side palms
  { x: 19, y: 6, tile: TileType.PalmTrunk },
  { x: 19, y: 5, tile: TileType.PalmLeaves },
  { x: 22, y: 15, tile: TileType.PalmTrunk },
  { x: 22, y: 14, tile: TileType.PalmLeaves },
  { x: 20, y: 24, tile: TileType.PalmTrunk },
  { x: 20, y: 23, tile: TileType.PalmLeaves },

  // Tiki bars — one per side, mid-route
  { x: 4, y: 18, tile: TileType.TikiBar },
  { x: 18, y: 10, tile: TileType.TikiBar },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildTileMaps(): { tileMap: number[][]; collisionMap: boolean[][] } {
  const tileMap: number[][] = [];
  const collisionMap: boolean[][] = [];

  for (let y = 0; y < HEIGHT; y++) {
    const tileRow: number[] = [];
    const collRow: boolean[] = [];
    for (let x = 0; x < WIDTH; x++) {
      const onBoardwalk = x >= BOARDWALK_X && x < BOARDWALK_X + BOARDWALK_W;
      if (onBoardwalk) {
        tileRow.push(TileType.Boardwalk);
        collRow.push(false);
      } else {
        // Grass flanks — walkable, but stepping triggers wild encounters.
        tileRow.push(TileType.Grass);
        collRow.push(false);
      }
    }
    tileMap.push(tileRow);
    collisionMap.push(collRow);
  }

  // Stamp props (palms + tiki bars) as impassable.
  for (const prop of PROPS) {
    if (prop.y < 0 || prop.y >= HEIGHT || prop.x < 0 || prop.x >= WIDTH) {
      continue;
    }
    tileMap[prop.y][prop.x] = prop.tile;
    // PalmLeaves overhang above the trunk — leaves are visually decorative
    // but we still block them so the player can't stand "inside" the tree.
    collisionMap[prop.y][prop.x] = true;
  }

  return { tileMap, collisionMap };
}

const { tileMap, collisionMap } = buildTileMaps();

// ─── Route 1 map export ───────────────────────────────────────────────────────

export const ROUTE1_MAP: RouteMap = {
  id: "route_1",
  name: "Route 1",
  type: "overworld",
  width: WIDTH,
  height: HEIGHT,
  tileMap,
  collisionMap,
  interactables: [],

  encounterZones: [
    {
      id: "route_1_west_grass",
      bounds: WEST_GRASS_BOUNDS,
      speciesPool: [
        { speciesId: "salespup", weight: 40, levelMin: 5, levelMax: 9 },
        { speciesId: "byte", weight: 30, levelMin: 5, levelMax: 10 },
        { speciesId: "vibecat", weight: 20, levelMin: 6, levelMax: 11 },
        { speciesId: "opster", weight: 10, levelMin: 8, levelMax: 12 },
      ],
    },
    {
      id: "route_1_east_grass",
      bounds: EAST_GRASS_BOUNDS,
      speciesPool: [
        { speciesId: "qubit", weight: 35, levelMin: 5, levelMax: 9 },
        { speciesId: "byte", weight: 30, levelMin: 6, levelMax: 10 },
        { speciesId: "salespup", weight: 25, levelMin: 5, levelMax: 9 },
        { speciesId: "opster", weight: 10, levelMin: 8, levelMax: 12 },
      ],
    },
  ],

  exits: [
    // South edge — back to the main overworld. Spawn the player just inside
    // the overworld map's north grass strip on the boardwalk column.
    {
      x: BOARDWALK_X + Math.floor(BOARDWALK_W / 2),
      y: HEIGHT - 1,
      targetMapId: "overworld",
      targetX: 40,
      targetY: 1,
    },
    // North edge — to town center. Spawn on the south side of the town map.
    {
      x: BOARDWALK_X + Math.floor(BOARDWALK_W / 2),
      y: 0,
      targetMapId: "town_center",
      targetX: 12,
      targetY: 22,
    },
  ],

  trainers: [
    // Two trainers stand on the boardwalk, blocking the path until defeated.
    { trainerId: "trainer_route1_a", x: BOARDWALK_X + 3, y: 10 },
    { trainerId: "trainer_route1_b", x: BOARDWALK_X + 4, y: 22 },
  ],
};
